<template>
  <div class="home-page">
    <!-- 欢迎横幅 -->
    <div class="welcome-banner">
      <div class="welcome-text">
        <div class="welcome-title">欢迎使用乐康护理服务管理系统</div>
        <div class="welcome-sub">用心服务 · 让长者安享晚年</div>
      </div>
      <div class="welcome-illustration">
        <i class="el-icon-s-home"></i>
      </div>
    </div>

    <!-- 数据概览卡片 -->
    <div class="stat-row">
      <div v-for="(item, idx) in statCards" :key="idx" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }">
          <i :class="item.icon"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ item.value }}</div>
          <div class="stat-label">{{ item.label }}</div>
        </div>
      </div>
    </div>

    <!-- 快捷入口 -->
    <div class="section">
      <div class="section-head">
        <span class="section-title">快捷入口</span>
      </div>
      <div class="quick-grid">
        <div
          v-for="(q, i) in quickEntries"
          :key="i"
          class="quick-item"
          @click="goTo(q.url)"
        >
          <div class="quick-icon" :style="{ background: q.bg }">
            <i :class="q.icon"></i>
          </div>
          <div class="quick-name">{{ q.name }}</div>
        </div>
      </div>
    </div>

    <!-- 服务类别 -->
    <div class="section">
      <div class="section-head">
        <span class="section-title">服务类别</span>
      </div>
      <div class="service-grid">
        <div
          v-for="(s, i) in visibleServices"
          :key="i"
          class="service-card"
          :class="{ clickable: !!s.url }"
          @click="s.url && goTo(s.url)"
        >
          <div class="service-card-icon" :style="{ color: s.color }">
            <i :class="s.icon"></i>
          </div>
          <div class="service-card-body">
            <div class="service-card-title">{{ s.title }}</div>
            <div class="service-card-desc">{{ s.desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 待办与公告 -->
    <div class="section section-double">
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">系统公告</span>
        </div>
        <ul class="notice-list">
          <li v-for="(n, i) in notices" :key="i">
            <span class="notice-dot"></span>
            <span class="notice-text">{{ n }}</span>
          </li>
        </ul>
      </div>
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">服务理念</span>
        </div>
        <div class="idea-box">
          <p>以长者为中心，提供专业、贴心、安全的居家与机构护理服务。</p>
          <p>坚持"以人为本、服务至上"，让每一位长者都能享受到有尊严、有温度的照护。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchStatisticsOrderQuantity, fetchFosterRecordList } from '@/api'
import { hasScreenPermission, isAdmin } from '../utils/util'

export default {
  name: 'Home',
  data() {
    return {
      statCards: [
        { label: '近7天订单量', value: 0, icon: 'el-icon-date', bg: 'rgba(38, 164, 178, 0.12)', color: '#26a4b2' },
        { label: '近30天订单量', value: 0, icon: 'el-icon-data-line', bg: 'rgba(82, 183, 136, 0.12)', color: '#52b788' },
        { label: '本年订单量', value: 0, icon: 'el-icon-pie-chart', bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
        { label: '本年度总服务次数', value: 0, icon: 'el-icon-s-marketing', bg: 'rgba(231, 76, 60, 0.12)', color: '#e74c3c' },
        { label: '累计订单总量', value: 0, icon: 'el-icon-files', bg: 'rgba(120, 108, 230, 0.12)', color: '#786ce6' }
      ],
      serviceList: [
        { title: '居家养老上门护理', desc: '专业护工上门，提供生活照料与基础护理', icon: 'el-icon-s-custom', color: '#26a4b2' },
        { title: '残疾人寄宿托养', desc: '日间照料与寄宿托养，关爱残疾人群体', icon: 'el-icon-s-home', color: '#52b788' },
        { title: '特困老人探访关爱', desc: '定期探访，关注特困老人生活与健康', icon: 'el-icon-s-flag', color: '#ff9f43' },
        { title: '失能人员护理', desc: '为失能人员提供专业护理服务', icon: 'el-icon-s-cooperation', color: '#786ce6' },
        { title: '智慧养老院', desc: '入院评估、每日照护、能力等级 0–4 与建议报告', icon: 'el-icon-s-help', color: '#1e7a84', url: '/screen' }
      ],
      notices: [
        '欢迎使用乐康护理服务管理系统，请及时核对订单信息。',
        '护工管理模块已上线，可在线指派与改派护工。',
        '请定期关注服务记录，确保服务质量。',
        '如遇系统问题，请联系管理员处理。'
      ]
    }
  },
  computed: {
    canScreen() {
      return hasScreenPermission(this.$store.state.rules)
    },
    visibleServices() {
      if (this.canScreen) return this.serviceList
      return this.serviceList.filter(s => s.url !== '/screen')
    },
    quickEntries() {
      const entries = []
      if (this.canScreen) {
        entries.push({ name: '智慧养老大屏', url: '/screen', icon: 'el-icon-monitor', bg: 'rgba(30, 122, 132, 0.14)' })
      }
      entries.push(
        { name: '订单管理', url: '/orders', icon: 'el-icon-s-order', bg: 'rgba(38, 164, 178, 0.12)' },
        { name: '财务统计', url: '/financial', icon: 'el-icon-money', bg: 'rgba(82, 183, 136, 0.12)' },
        { name: '护工管理', url: '/nurse-management', icon: 'el-icon-user', bg: 'rgba(255, 159, 67, 0.12)' },
        { name: '数据统计', url: '/statistics', icon: 'el-icon-data-analysis', bg: 'rgba(120, 108, 230, 0.12)' }
      )
      if (isAdmin()) {
        entries.push({ name: '账号管理', url: '/account', icon: 'el-icon-setting', bg: 'rgba(231, 76, 60, 0.12)' })
      }
      return entries
    }
  },
  mounted() {
    this.loadStats()
  },
  methods: {
    loadStats() {
      fetchStatisticsOrderQuantity().then(res => {
        if (res && res.status && res.data) {
          const { last_seven_days, last_thirty_days, last_year_days, order_total } = res.data
          this.statCards[0].value = last_seven_days || 0
          this.statCards[1].value = last_thirty_days || 0
          this.statCards[2].value = last_year_days || 0
          this.statCards[4].value = order_total || 0
        }
      }).catch(() => {})
      // 本年度总服务次数：按服务时间筛选本年度服务记录总数
      const year = new Date().getFullYear()
      const startAt = `${year}-01-01 00:00:00`
      const endAt = `${year}-12-31 23:59:59`
      fetchFosterRecordList({
        page_size: 1,
        page_number: 1,
        dis_start_at: startAt,
        dis_end_at: endAt
      }).then(res => {
        if (res && res.status && res.data) {
          this.statCards[3].value = res.data.total || 0
        }
      }).catch(() => {})
    },
    goTo(url) {
      if (url) this.$router.push(url).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  padding: 16px 20px 30px;
}

/* 欢迎横幅 */
.welcome-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #26a4b2 0%, #2dc8a3 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(38, 164, 178, 0.18);
}
.welcome-title {
  font-size: 22px;
  font-weight: 600;
}
.welcome-sub {
  margin-top: 8px;
  font-size: 14px;
  opacity: 0.9;
}
.welcome-illustration i {
  font-size: 64px;
  opacity: 0.85;
}

/* 数据概览 */
.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 20px;
}
.stat-card {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  i { font-size: 24px; }
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}
.stat-label {
  margin-top: 4px;
  font-size: 13px;
  color: #909399;
}

/* 通用 section */
.section {
  margin-top: 24px;
}
.section-head {
  padding-bottom: 12px;
  border-bottom: 1px solid #ececec;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  position: relative;
  padding-left: 10px;
}
.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 15px;
  background: #26a4b2;
  border-radius: 2px;
}

/* 快捷入口 */
.quick-grid {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.quick-item {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(38, 164, 178, 0.12);
    transform: translateY(-2px);
  }
}
.quick-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  i { font-size: 26px; color: #26a4b2; }
}
.quick-name {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

/* 服务类别 */
.service-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.service-card {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  transition: all 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  &.clickable {
    cursor: pointer;
    &:hover {
      box-shadow: 0 4px 12px rgba(30, 122, 132, 0.12);
      transform: translateY(-2px);
    }
  }
}
.service-card-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  i { font-size: 30px; }
}
.service-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.service-card-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 双栏面板 */
.section-double {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 20px;
}
.panel-head {
  padding-bottom: 12px;
  border-bottom: 1px solid #f2f6fc;
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  position: relative;
  padding-left: 10px;
}
.panel-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 14px;
  background: #26a4b2;
  border-radius: 2px;
}
.notice-list {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px dashed #f2f6fc;
    &:last-child { border-bottom: none; }
  }
}
.notice-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #26a4b2;
  margin-right: 10px;
  flex-shrink: 0;
}
.notice-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.idea-box {
  padding: 8px 4px;
  p {
    margin: 0 0 12px 0;
    font-size: 13px;
    color: #606266;
    line-height: 1.8;
    &:last-child { margin-bottom: 0; }
  }
}

@media (max-width: 768px) {
  .section-double {
    grid-template-columns: 1fr;
  }
  .welcome-illustration { display: none; }
}
</style>
